import { Component, computed, effect, signal, WritableSignal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { KYBPrefill, KYBPrefillField } from '@app/models/kyc-kyb/types';
import { FormErrorsPipe } from '@app/pipes';
import { AuthService } from '@app/services';
import { BaseForm } from '@forms/base.form';
import { TranslateModule } from '@ngx-translate/core';

const REQUIRED_FIELDS = ['firstName', 'lastName', 'jobTitle', 'emailAddress'] as const;
type RequiredField = (typeof REQUIRED_FIELDS)[number];

/**
 * Maps the name of the control in the form to the field name that is sent to Persona
 */
const PERSONA_FIELD_MAPPING: Record<RequiredField | 'isSigningOfficer', KYBPrefillField> = {
    firstName: 'form_filler_name_first',
    lastName: 'form_filler_name_last',
    jobTitle: 'form_filler_job_title',
    emailAddress: 'form_filler_email_address',
    isSigningOfficer: 'form_filler_is_signing_officer',
};

@Component({
    standalone: true,
    selector: 'app-kyb-signing-officer-form',
    imports: [
        ReactiveFormsModule,
        FormsModule,
        TranslateModule,
        MatInputModule,
        MatFormFieldModule,
        MatRadioModule,
        FormErrorsPipe,
    ],
    template: `<form class="flex col" #form="ngForm">
        @for (
            description of 'payroll.kyc-kyb.pre-redirect.KYB.signing-officer-dialog.description' | translate;
            track $index
        ) {
            <div [class.margin-top-2]="$index > 0">
                {{ description }}
            </div>
        }
        <mat-radio-group name="isSigningOfficer" class="vertical margin-y-3" [(ngModel)]="isSigningOfficer">
            <mat-radio-button [value]="true">{{
                'payroll.kyc-kyb.pre-redirect.KYB.signing-officer-dialog.yes' | translate
            }}</mat-radio-button>
            <mat-radio-button [value]="false">{{
                'payroll.kyc-kyb.pre-redirect.KYB.signing-officer-dialog.no' | translate
            }}</mat-radio-button>
        </mat-radio-group>
        @if (isSigningOfficer() !== undefined) {
            @if (!isSigningOfficer()) {
                <div class="margin-bottom-2">
                    {{ 'payroll.kyc-kyb.pre-redirect.KYB.signing-officer-dialog.pleaseProvide' | translate }}
                </div>
            }
            <div class="responsive-grid">
                <div class="row">
                    <mat-form-field>
                        <mat-label>{{
                            'payroll.kyc-kyb.pre-redirect.KYB.signing-officer-dialog.firstName' | translate
                        }}</mat-label>
                        <input [disabled]="!!isSigningOfficer()" matInput name="firstName" [(ngModel)]="firstName" />
                    </mat-form-field>
                    <mat-form-field>
                        <mat-label>{{
                            'payroll.kyc-kyb.pre-redirect.KYB.signing-officer-dialog.lastName' | translate
                        }}</mat-label>
                        <input [disabled]="!!isSigningOfficer()" matInput name="lastName" [(ngModel)]="lastName" />
                    </mat-form-field>
                </div>
            </div>
            <mat-form-field>
                <mat-label>{{
                    'payroll.kyc-kyb.pre-redirect.KYB.signing-officer-dialog.jobTitle' | translate
                }}</mat-label>
                <input [disabled]="!!isSigningOfficer()" matInput name="jobTitle" [(ngModel)]="jobTitle" />
            </mat-form-field>
            <mat-form-field>
                <mat-label>{{
                    'payroll.kyc-kyb.pre-redirect.KYB.signing-officer-dialog.emailAddress' | translate
                }}</mat-label>
                <input
                    #email="ngModel"
                    [disabled]="!!isSigningOfficer()"
                    matInput
                    name="emailAddress"
                    [(ngModel)]="emailAddress"
                    email
                />
                @if (email.invalid) {
                    <mat-error>{{ email.errors | formErrors }}</mat-error>
                }
            </mat-form-field>
        }
    </form>`,
})
export class KYBSigningOfficerForm extends BaseForm implements Record<RequiredField, WritableSignal<string>> {
    isSigningOfficer = signal<boolean | undefined>(undefined);
    firstName = signal<string>('');
    lastName = signal<string>('');
    jobTitle = signal<string>('');
    emailAddress = signal<string>('');

    /**
     * Form is only valid if every field has a non-empty value
     */
    isFormValid = computed(() => REQUIRED_FIELDS.every((field) => this[field]()));

    /**
     * Automatically fills or clears the form based on the radio button value
     */
    fillForm = effect(
        () => {
            if (this.isSigningOfficer() === true) {
                const { account, employee } = this.authService;
                this.firstName.set(account.legalFirstName);
                this.lastName.set(account.legalLastName);
                this.jobTitle.set(employee.job?.title ?? '');
                this.emailAddress.set(account.email);
            } else {
                this.firstName.set('');
                this.lastName.set('');
                this.jobTitle.set('');
                this.emailAddress.set('');
            }
        },
        { allowSignalWrites: true }
    );

    constructor(private authService: AuthService) {
        super();
    }

    onSubmit(): KYBPrefill | undefined {
        this.form.onSubmit(new SubmitEvent('submit'));

        if (!this.form.valid) {
            return;
        }

        // Maps the form values to the expected mapping by Persona
        return REQUIRED_FIELDS.reduce(
            (acc, cur) => {
                return { ...acc, [PERSONA_FIELD_MAPPING[cur]]: this.form.controls[cur].value };
            },
            // eslint-disable-next-line camelcase
            { form_filler_is_signing_officer: this.form.controls['isSigningOfficer'].value } as KYBPrefill
        );
    }
}
